import React, { useMemo } from "react";
import { Lock } from "@mui/icons-material";
import { Box } from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";

import { useGetVersionScenarioConfigurationQuery } from "fond/api";
import { Project } from "fond/types";
import { useAppSelector } from "fond/utils/hooks";
import { Actions, permissionCheck } from "fond/utils/permissions";
import { AccordionStepper, AccordionStepperPane, NonIdealState } from "fond/widgets";

import GeneratePanel from "./GeneratePanel";
import SetupPanel from "./SetupPanel";

interface CollaborationCTSPanelProps {
  /**
   * The project currently being viewed / edited
   */
  project: Project;
}

const CollaborationCTSPanel: React.FC<CollaborationCTSPanelProps> = ({ project }) => {
  const versionId = useAppSelector((state) => state.project.versionId);
  const { data: scenarioConfiguration } = useGetVersionScenarioConfigurationQuery(versionId ?? skipToken);

  const accordionPanes: AccordionStepperPane[] = useMemo(
    () => [
      {
        header: "Setup cost to serve",
        id: "boundary",
        content: <SetupPanel configuration={scenarioConfiguration} />,
        isComplete: !!scenarioConfiguration,
        isLoading: false, // TODO: define completion criteria
      },
      {
        header: "Generate",
        id: "generate",
        content: <GeneratePanel versionId={versionId} />,
        isComplete: false, // TODO: define completion criteria
        isLoading: false, // TODO: define completion criteria
      },
      {
        header: "Analysis",
        id: "analysis",
        content: null,
        isComplete: false, // TODO: define completion criteria
        isLoading: false, // TODO: define completion criteria
      },
    ],
    [scenarioConfiguration]
  );

  if (!project) return null;

  return (
    <Box data-testid="collaboration-cts-panel" sx={{ height: "100%" }}>
      {!permissionCheck(project.Permission?.Level, Actions.PROJECT_EDIT) ? ( // if user has access and no license, more granular "locks" are applied per component
        <NonIdealState size="small" icon={<Lock />} title="View only access" description="You only have view access to this project" />
      ) : (
        <AccordionStepper panes={accordionPanes} id={project.ID} />
      )}
    </Box>
  );
};

export default CollaborationCTSPanel;
